import React, { useEffect, useState } from 'react';
import { supportConfig } from '../../../config/app.config';

// EX-02: resend throttle
export const RESEND_COOLDOWN_SECONDS = 60;
export const MAX_RESENDS = 3;

interface EmailVerificationProps {
  email: string;
  verificationCode: string;
  setVerificationCode: (code: string) => void;
  handleVerifyEmail: () => void;
  handleResendCode: () => void;
  /** Leave verification and return to sign-in */
  onBack?: () => void;
  error: string | null;
  isLoading: boolean;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  verificationCode,
  setVerificationCode,
  handleVerifyEmail,
  handleResendCode,
  onBack,
  error,
  isLoading
}) => {
  const [resendCount, setResendCount] = useState(0);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const onResend = () => {
    if (cooldown > 0 || resendCount >= MAX_RESENDS) return;
    setResendCount((n) => n + 1);
    setCooldown(RESEND_COOLDOWN_SECONDS);
    handleResendCode();
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleVerifyEmail();
  };

  return (
    <div className="w-full max-w-sm py-8 md:py-10">
      <h2 className="text-2xl font-semibold mb-1 text-slate-800 text-left">Verify your email</h2>
      <p className="text-gray-500 mb-6 text-sm text-left">
        We've sent a verification code to <strong>{email}</strong>
      </p>
      
      {error && <div className="p-3 bg-red-500/10 border-l-4 border-red-500 text-red-500 rounded mb-6 text-sm">{error}</div>}
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm text-left" htmlFor="verificationCode">
            Verification code
          </label>
          <input
            className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
            type="text"
            id="verificationCode"
            placeholder="Enter the 6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
          />
        </div>
        <button 
          type="submit"
          className={`w-full py-3 px-4 bg-slate-900 text-white rounded-md text-sm font-medium transition ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-slate-800'}`}
          disabled={isLoading}
        >
          {isLoading ? 'Verifying...' : 'Verify email'}
        </button>
        
        <div className="text-center text-sm">
          {resendCount < MAX_RESENDS ? (
            <p>
              Didn't receive the code? {' '}
              <button 
                type="button"
                onClick={onResend}
                className={`font-medium ${cooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'}`}
                disabled={isLoading || cooldown > 0}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
              </button>
            </p>
          ) : (
            <p data-testid="resend-support">
              Still no code?{' '}
              {supportConfig.email ? (
                <a href={`mailto:${supportConfig.email}`} className="text-blue-600 hover:text-blue-700 font-medium">
                  Contact support
                </a>
              ) : (
                <span className="font-medium">Contact support</span>
              )}
            </p>
          )}
        </div>
      </form>

      {onBack && (
        <div className="mt-6 text-center text-sm">
          <button type="button" onClick={onBack} className="text-blue-600 hover:text-blue-700 font-medium">
            Back to sign in
          </button>
        </div>
      )}
      
      <div className="mt-8 p-4 border border-slate-200 rounded-md bg-slate-50">
        <p className="text-sm text-gray-600">
          <strong>Tip:</strong> Check your spam or junk folder if you don't see the email in your inbox.
        </p>
      </div>
    </div>
  );
};

export default EmailVerification;
